import { shouldDisplayReviews } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { SearchManagerOutput } from '@/hooks/search/useSearch';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import { SearchState } from '@/reducers/searchV2Reducer';
import { Tracking } from '@/types/analytics';
import { SearchPlace } from '@/types/api/services/search';
import { FILTER_ID, UrlSearchState } from '@/types/state/search';
import {
  CAMPAIGNS_SHOWN,
  SEARCH_FILTERS,
  SEARCH_LOCATION_FILTER,
  SEARCH_TIME_FILTER,
  SORTED_BY,
} from '../constants/tracking';
import {
  CampaignsShown,
  SearchContext,
  SearchFilters,
  SearchLocationFilter,
  SearchTimeFilter,
  SortedBy,
  TrackingProps,
} from '../types/tracking';

export function trackSearchPerformed(properties: TrackingProps) {
  trackMpEvent({ name: 'search_performed', properties });
}

export function getSearchPerformedTrackingProps({
  urlState,
  searchState,
  context,
  screenName,
}: {
  urlState: UrlSearchState;
  searchState: SearchState;
  context: SearchContext;
  screenName: Tracking.ScreenName;
}): TrackingProps {
  return {
    screen_name: screenName,
    search_context: context,
    search_query: urlState?.q.trim?.().length > 0 ? urlState.q : undefined,
    search_location_filter: getSearchLocationFilter(urlState),
    search_filters: getSearchFilters(urlState.prefs),
    sorted_by: getSortedBy(urlState),
    search_time_filter: getSearchTimeFilter(urlState),
    campaigns_shown: getCampaignsShown(searchState),
    search_location_city: getSearchLocationCity(urlState),
    nbr_search_results: searchState.results > 0 ? searchState.results : undefined,
    location_source: urlState?.locationSource ?? undefined,
  };
}

function getCampaignsShown(state: SearchState): CampaignsShown | undefined {
  const campaigns = [];

  if (state.topSearch?.places?.length > 0) {
    campaigns.push(CAMPAIGNS_SHOWN.RAKETEN);
  }

  if (state.isSistaminutenSearch) {
    campaigns.push(CAMPAIGNS_SHOWN.SISTAMINUTEN);
  }

  return campaigns.length > 0 ? campaigns : undefined;
}

function getSearchLocationCity(state: UrlSearchState): string[] | undefined {
  const locations = [];

  if (state.location) {
    locations.push(state.location);
  }

  return locations.length > 0 ? locations : undefined;
}

function getSearchTimeFilter(state: UrlSearchState): SearchTimeFilter {
  if (state.timeOfDay || state.startDate || state.endDate) {
    return SEARCH_TIME_FILTER.USER_INPUT;
  }

  return SEARCH_TIME_FILTER.DEFAULT;
}

function getSearchLocationFilter(state: UrlSearchState): SearchLocationFilter {
  if (state.location === DEFAULT_MY_LOCATION_KEYWORD) {
    return SEARCH_LOCATION_FILTER.GPS_LOCATION;
  } else if (state.location) {
    return SEARCH_LOCATION_FILTER.USER_INPUT;
  }

  return undefined;
}

function getSortedBy(state: UrlSearchState): SortedBy {
  switch (state.sort) {
    case 'popular':
      return SORTED_BY.POPULAR;
    case 'closest':
      return SORTED_BY.CLOSEST;
    case 'reviews_score':
      return SORTED_BY.HIGHEST_RATING;
    default:
      return SORTED_BY.POPULAR;
  }
}

function getSearchFilters(state: UrlSearchState['prefs']): SearchFilters | undefined {
  const prefs = (state?.split(',') ?? []).map((pref) => +pref);

  const filters = [];

  for (const pref of prefs) {
    if (pref === FILTER_ID.WELLNESSCARD) {
      filters.push(SEARCH_FILTERS.WELLNESS_CARD);
    }

    if (pref === FILTER_ID.GIFTCARD) {
      filters.push(SEARCH_FILTERS.GIFT_CARD);
    }

    if (pref === FILTER_ID.PAY_LATER) {
      filters.push(SEARCH_FILTERS.PAY_LATER);
    }

    if (pref === FILTER_ID.CERTIFIED) {
      filters.push(SEARCH_FILTERS.ASSOCIATIONS);
    }

    if (pref === FILTER_ID.CAMPAIGN) {
      filters.push(SEARCH_FILTERS.CAMPAIGNS);
    }

    if (pref === FILTER_ID.BUNDLE) {
      filters.push(SEARCH_FILTERS.BUNDLES);
    }
  }

  return filters.length > 0 ? filters : undefined;
}

export function getPageScreenName(
  type: SearchType,
  currentView: 'list' | 'map',
  isMobileView: boolean,
): Tracking.ScreenName {
  switch (type) {
    case 'default':
      return isMobileView && currentView !== 'list' ? 'search_results_map' : 'search_results';
    case 'sistaminuten':
      return isMobileView && currentView !== 'list' ? 'deals_map' : 'deals';
    default:
      const _exhaustiveCheck: never = type;
      throw new Error(`Unhandled type: ${_exhaustiveCheck}`);
  }
}

export function trackPlaceClick(
  searchManager: SearchManagerOutput,
  place: SearchPlace,
  index: number,
  screenName: Tracking.ScreenName,
  isTopOfSearch: boolean = false,
) {
  let number_of_ratings = undefined;
  let ratings_average = undefined;

  if (shouldDisplayReviews(place) && place?.rating?.count > 4) {
    ratings_average = place.rating.score.toFixed(1);
    number_of_ratings = place.rating.count;
  }

  const campaings_shown = getCampaignsShown(searchManager);

  if (!isTopOfSearch && campaings_shown?.includes(CAMPAIGNS_SHOWN.RAKETEN)) {
    const indexOfRaketen = campaings_shown.indexOf(CAMPAIGNS_SHOWN.RAKETEN);
    if (indexOfRaketen > -1) {
      campaings_shown.splice(indexOfRaketen, 1);
    }
  }

  trackMpEvent({
    name: 'company_clicked',
    properties: {
      screen_name: screenName,
      marketplace_id: place.id,
      salon_id: place.portalId,
      number_of_ratings,
      ratings_average,
      position: index,
      search_query: searchManager?.q.trim?.().length > 0 ? searchManager.q : undefined,
      search_location_filter: getSearchLocationFilter(searchManager),
      campaigns_shown: campaings_shown,
      search_location_city: getSearchLocationCity(searchManager),
      nbr_search_results: searchManager.results > 0 ? searchManager.results : undefined,
    },
  });
}
